import { OrderProps, useOrderB2c } from 'hooks/useOrderB2c'
import { getPayableAmount } from 'utils/reshipping'
import { ArrowLeft, CreditCard, Receipt, Wallet } from 'lucide-react'
import { useEffect, useState } from 'react'
import Boleto from './Boleto'
import { CreditCardComponent } from './CreditCart'
import * as S from './styles'

import PaymentMethodSelector from './PaymentMethodSelector'
import { Pix } from './Pix'
import { PagalevePayment } from './Pagaleve/index'

interface PaymentMethodProps {
  disabledPix?: boolean
  disabledCreditCard?: boolean
  disabledBoleto?: boolean
  error: boolean
  isValidateSubmit?: boolean
  loadingContainer: boolean
  clearIntervalOutside: () => void
  hasMultipleMethods?: boolean
  order: OrderProps
}

export interface BarcodeInfoProps {
  amount: number
  boleto_barcode: string
  boleto_expiration_date: Date
  boleto_url: string
}


export const PaymentMethod = ({
  disabledPix,
  disabledCreditCard,
  disabledBoleto,
  error,
  isValidateSubmit = false,
  loadingContainer,
  clearIntervalOutside,
  hasMultipleMethods,
  order
}: PaymentMethodProps) => {

  const { setError } = useOrderB2c()
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null)
  const [showSelector, setShowSelector] = useState(true)
  const [isProcessingPayment, setIsProcessingPayment] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const lastOrderCreated  = order

  useEffect(() => {
    return () => {
      clearIntervalOutside()
    }
  }, [clearIntervalOutside])

  const handleMethodSelect = (method: string) => {
    setSelectedMethod(method)
    setShowSelector(false)
  }

  const handleBackToSelector = () => {
    if (!isProcessingPayment && !isSubmitting) {
      setShowSelector(true)
      setSelectedMethod(null)
    }
  }

  const handleSubmittingChange = (submitting: boolean) => {
    setIsSubmitting(submitting);
  }

  const handleProcessingChange = (processing: boolean) => {
    setIsProcessingPayment(processing);
  }

  const getMethodIcon = (method: string) => {
    switch (method) {
      case 'credit_card':
      case 'credit_card_credit_card':
      case 'credit_card_boleto':
        return <CreditCard size={24} />
      case 'pix':
      case 'pagaleve':
      case 'credit_card_pix':
        return <Wallet size={24} />
      case 'boleto':
        return <Receipt size={24} />
      default:
        return null
    }
  }

  const getMethodTitle = (method: string) => {
    switch (method) {
      case 'credit_card':
        return 'Cartão de Crédito'
      case 'pix':
        return 'Pix'
      case 'pagaleve':
        return 'PIX Parcelado'
      case 'boleto':
        return 'Boleto Bancário'
      case 'credit_card_credit_card':
        return 'Dois Cartões de Crédito'
      case 'credit_card_pix':
        return 'Cartão de Crédito + Pix'
      case 'credit_card_boleto':
        return 'Cartão de Crédito + Boleto'
      default:
        return ''
    }
  }

  const renderPaymentComponent = () => {
    switch (selectedMethod) {
      case 'credit_card':
      case 'credit_card_credit_card':
      case 'credit_card_pix':
      case 'credit_card_boleto':
        return (
          <CreditCardComponent
            order={order}
            loadingContainer={loadingContainer}
            paymentMethod={
              selectedMethod as 'credit_card' | 'credit_card_credit_card' | 'credit_card_pix' | 'credit_card_boleto'
            }
            onSubmittingChange={handleSubmittingChange}
            onProcessingChange={handleProcessingChange}
          />
        )
      case 'pix':
        return <Pix loadingContainer={loadingContainer} order={order} onProcessingChange={handleProcessingChange}/>
      case 'pagaleve':
        return <PagalevePayment order={order} onProcessingChange={handleProcessingChange} />
      case 'boleto':
        return <Boleto isValidateSubmit={isValidateSubmit} setError={setError} order={order} onProcessingChange={handleProcessingChange}/>
      default:
        return null
    }
  }

  console.log(typeof lastOrderCreated.amount)

  return (
    <S.Container>
      {showSelector ? (
        <PaymentMethodSelector
          selectedMethod={selectedMethod}
          onSelectMethod={handleMethodSelect}
          totalAmount={getPayableAmount(lastOrderCreated)}
          disabledPix={disabledPix}
          disabledCreditCard={disabledCreditCard}
          disabledBoleto={disabledBoleto}
          hasMultipleMethods={hasMultipleMethods}
          applyPixDiscountOrder={lastOrderCreated?.apply_pix_discount}
        />
      ) : (
        <S.PaymentFormContainer>
          <S.PaymentFormHeader>
          <S.BackButton
              onClick={handleBackToSelector}
              style={{ color: '#748351' }}
              disabled={isProcessingPayment || isSubmitting}
            >
              <ArrowLeft
                size={24}
                style={{ color: (isProcessingPayment || isSubmitting) ? '#ccc' : '#748351' }}
              />
              Formas de pagamento
            </S.BackButton>
            <S.SelectedMethodInfo>
              <span style={{ color: '#748351' }}>{getMethodIcon(selectedMethod || '')}</span>
              <span>{getMethodTitle(selectedMethod || '')}</span>
            </S.SelectedMethodInfo>
          </S.PaymentFormHeader>
          {renderPaymentComponent()}
        </S.PaymentFormContainer>
      )}
    </S.Container>
  )
}
